import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Bell,
  FileBarChart,
  LayoutDashboard,
  MessageSquareText,
  Truck,
  Users,
} from "lucide-react";
import { TabBar, type TabBarItem } from "@/components/ui/TabBar";
import type { AnalyticsTab } from "@/features/reports/analyticsFilters";

const TAB_META: { value: AnalyticsTab; icon: typeof BarChart3 }[] = [
  { value: "dashboard", icon: LayoutDashboard },
  { value: "plan-vs-actual", icon: BarChart3 },
  { value: "drivers", icon: Users },
  { value: "reviews", icon: MessageSquareText },
  { value: "fleet-load", icon: Truck },
  { value: "notifications", icon: Bell },
  { value: "reports", icon: FileBarChart },
];

const TAB_LABEL_KEY: Record<AnalyticsTab, string> = {
  dashboard: "analytics.tabs.dashboard",
  "plan-vs-actual": "analytics.tabs.planVsActual",
  drivers: "analytics.tabs.drivers",
  reviews: "analytics.tabs.reviews",
  "fleet-load": "analytics.tabs.fleetLoad",
  notifications: "analytics.tabs.notifications",
  reports: "analytics.tabs.reports",
};

interface Props {
  active: AnalyticsTab;
  onChange: (tab: AnalyticsTab) => void;
}

/**
 * Pill tab group for the consolidated Analytics page (Sprint 117, TASK-3828).
 * Seven tabs in fixed order; horizontally scrollable on narrow viewports. Active
 * tab comes from `?tab=` resolved by the shell.
 *
 * Sprint 121 (TASK-3859): thin wrapper over the shared, presentational
 * `<TabBar>` (TASK-3858) — the canonical pill style. This component owns only
 * the tab metadata (`TAB_META`) and label translation, then delegates all
 * rendering, overflow affordance, and a11y to `<TabBar>`.
 */
export function AnalyticsTabBar({ active, onChange }: Props) {
  const { t } = useTranslation();

  const tabs: TabBarItem[] = TAB_META.map(({ value, icon }) => ({
    value,
    label: t(TAB_LABEL_KEY[value]),
    icon,
  }));

  return (
    <TabBar
      tabs={tabs}
      active={active}
      onChange={(v) => onChange(v as AnalyticsTab)}
      ariaLabel={t("analytics.title")}
    />
  );
}
